import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import {
  Button,
  Grid,
  TextField,
  MenuItem,
  Stack,
  Skeleton
} from "@mui/material";
import SaveAsIcon from "@mui/icons-material/SaveAs";
import Modal from "../../Components/Modal";
import { establishments } from "../../Requests";
import useGlobalContext from "../../Hooks/useGlobalContext";
import { useSnackbar } from "notistack";

interface IPreserveEstablishmentProps {
  reloadResourceTable: () => void;
  openModal: boolean;
  setOpenModal: Dispatch<SetStateAction<boolean>>;
  idEdit: number | null;
}

interface Ifield<T> {
  value: T;
  helperText: string;
  error: boolean;
}

// Funções utilitárias de formatação
const formatCnpj = (value: string): string => {
  const v = value.replace(/\D/g, "");
  if (v.length <= 2) return v;
  if (v.length <= 5) return v.replace(/^(\d{2})(\d+)/, "$1.$2");
  if (v.length <= 8) return v.replace(/^(\d{2})(\d{3})(\d+)/, "$1.$2.$3");
  if (v.length <= 12) return v.replace(/^(\d{2})(\d{3})(\d{3})(\d+)/, "$1.$2.$3/$4");
  return v.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d+).*/, "$1.$2.$3/$4-$5");
};

const formatStateRegistration = (value: string): string => value.replace(/\D/g, "");

const PreserveEstablishment: React.FC<IPreserveEstablishmentProps> = ({
  reloadResourceTable,
  openModal,
  setOpenModal,
  idEdit
}) => {
  const { setLoading, loading } = useGlobalContext();
  const { enqueueSnackbar } = useSnackbar();
  const corporateNameRef = useRef<HTMLInputElement>(null);

  const [enableButton, setEnableButton] = useState(false);

  const [description, setDescription] = useState<Ifield<string>>({ value: "", helperText: "", error: false });
  const [document, setDocument] = useState<Ifield<string>>({ value: "", helperText: "", error: false });
  const [corporateName, setCorporateName] = useState<Ifield<string>>({ value: "", helperText: "", error: false });
  const [stateRegistration, setStateRegistration] = useState<Ifield<string>>({ value: "", helperText: "", error: false });
  const [status, setStatus] = useState<Ifield<string>>({ value: "active", helperText: "", error: false });

  const handleChange = {
    description: (e: React.ChangeEvent<HTMLInputElement>) =>
      setDescription({ value: e.target.value, helperText: "", error: false }),
    document: (e: React.ChangeEvent<HTMLInputElement>) => {
      const formatted = formatCnpj(e.target.value);
      setDocument({ value: formatted, helperText: "", error: false });
    },
    corporateName: (e: React.ChangeEvent<HTMLInputElement>) =>
      setCorporateName({ value: e.target.value, helperText: "", error: false }),
    stateRegistration: (e: React.ChangeEvent<HTMLInputElement>) => {
      const formatted = formatStateRegistration(e.target.value);
      setStateRegistration({ value: formatted, helperText: "", error: false });
    },
    status: (e: React.ChangeEvent<HTMLInputElement>) =>
      setStatus({ value: e.target.value, helperText: "", error: false })
  };

  const validateFields = () => !!description.value && !!corporateName.value && !!status.value;

  const updateButtonState = () => setEnableButton(validateFields());

  const handleSave = async () => {
    setLoading?.(true);

    const payload: any = {
      description: description.value,
      document: document.value,
      corporate_name: corporateName.value,
      state_registration: stateRegistration.value,
      status: status.value
    };

    const resultQuery = (
        idEdit === null
        ? await establishments.post(payload)
        : await establishments.put(`${idEdit}`, payload)
    );

    const result = resultQuery?.data || resultQuery;

    if (result?.errors) {
      // Mapeia cada campo de erro ao respectivo setter de estado
      const fieldSetterMap: Record<string, (msgArray: string[]) => void> = {
        description: (msgs) => setDescription({ value: description.value, helperText: msgs.join("\n"), error: true }),
        document: (msgs) => setDocument({ value: document.value, helperText: msgs.join("\n"), error: true }),
        corporate_name: (msgs) => setCorporateName({ value: corporateName.value, helperText: msgs.join("\n"), error: true }),
        state_registration: (msgs) => setStateRegistration({ value: stateRegistration.value, helperText: msgs.join("\n"), error: true }),
        status: (msgs) => setStatus({ value: status.value, helperText: msgs.join("\n"), error: true })
      };

      Object.entries(result.errors).forEach(([field, messages]) => {
        const setter = fieldSetterMap[field];
        if (setter && Array.isArray(messages)) {
          setter(messages as string[]);
        }
      });

      setEnableButton(false);

      enqueueSnackbar(result?.message ?? "Erro de validação.", { variant: "error" });
    }

    

    if (result?.identifier || result?.description) {
      const successMessage = idEdit === null
        ? "Estabelecimento cadastrado com sucesso!"
        : "Estabelecimento atualizado com sucesso!";

      enqueueSnackbar(successMessage, { variant: "success" });
      handleCloseModal();
      reloadResourceTable();
    } else if (result?.message) {
      enqueueSnackbar(result.message, { variant: "error" });
    } else {
      const errorMessage = idEdit === null
        ? "Erro ao cadastrar estabelecimento."
        : "Erro ao atualizar estabelecimento.";
      enqueueSnackbar(errorMessage, { variant: "error" });
    }

    setLoading?.(false);
  };

  const handleCloseModal = () => setOpenModal(false);

  const reloadForm = async () => {
    setDescription({ value: "", helperText: "", error: false });
    setCorporateName({ value: "", helperText: "", error: false });
    setStateRegistration({ value: "", helperText: "", error: false });
    setDocument({ value: "", helperText: "", error: false });
    setStatus({ value: "active", helperText: "", error: false });

    if (idEdit) {
      const { data } = await establishments.get(`${idEdit}`);
      setDescription({ value: data.description, helperText: "", error: false });
      setDocument({ value: data.document ?? "", helperText: "", error: false });
      setCorporateName({ value: data.corporate_name ?? "", helperText: "", error: false });
      setStateRegistration({ value: data.state_registration ?? "", helperText: "", error: false });
      setStatus({ value: data.status ?? "active", helperText: "", error: false });
    }

    setTimeout(() => corporateNameRef?.current?.focus(), 100);
    setLoading?.(false);
  };

  useEffect(() => {
    if (openModal) reloadForm();
  }, [openModal]);

  useEffect(() => {
    updateButtonState();
  }, [description, document, corporateName, stateRegistration, status]);

  return (
    <Modal
      title={idEdit ? "Editar estabelecimento" : "Adicionar novo estabelecimento"}
      open={openModal}
      closeModal={handleCloseModal}
      maxWidth="sm"
    >
      <Grid container spacing={0.5}>
        <Grid item xs={12}>
          <TextField
            id="corporate_name"
            label="Razão social"
            variant="filled"
            value={corporateName.value}
            onChange={handleChange.corporateName}
            inputRef={corporateNameRef}
            fullWidth
            error={corporateName.error}
            helperText={corporateName.helperText}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            id="description"
            label="Nome fantasia"
            variant="filled"
            value={description.value}
            onChange={handleChange.description}
            fullWidth
            error={description.error}
            helperText={description.helperText}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            id="document"
            label="CNPJ"
            variant="filled"
            value={document.value}
            onChange={handleChange.document}
            fullWidth
            error={document.error}
            helperText={document.helperText}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            id="state_registration"
            label="Inscrição Estadual"
            variant="filled"
            value={stateRegistration.value}
            onChange={handleChange.stateRegistration}
            fullWidth
            error={stateRegistration.error}
            helperText={stateRegistration.helperText}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            id="status"
            select
            label="Status"
            variant="filled"
            value={status.value}
            onChange={handleChange.status}
            fullWidth
          >
            <MenuItem value="active">Ativo</MenuItem>
            <MenuItem value="inactive">Inativo</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button variant="outlined" onClick={handleCloseModal} aria-label="Cancelar">
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={loading ? <Skeleton variant="circular" width={20} height={20} sx={{ bgcolor: "#FFF" }} /> : <SaveAsIcon />}
              onClick={handleSave}
              disabled={!enableButton || loading}
              aria-label="Salvar estabelecimento"
            >
              {loading ? "Salvando..." : "Salvar"}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default PreserveEstablishment; 